import { User, UserInput } from "@/types/crm";

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

class UsersService {
  private getHeaders(): HeadersInit {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  }

  async getUsers(
    companySlug: string,
    page = 1,
    limit = 50,
    is_active?: boolean,
  ): Promise<{
    data: User[];
    pagination: { page: number; limit: number; total: number };
  }> {
    try {
      const params = new URLSearchParams({
        slug: companySlug,
        page: page.toString(),
        limit: limit.toString(),
      });

      if (is_active !== undefined) {
        params.append("is_active", is_active.toString());
      }

      const response = await fetch(`${API_BASE_URL}/users?${params.toString()}`, {
        method: "GET",
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && Array.isArray(apiResponse.data)) {
        return {
          data: apiResponse.data,
          pagination: apiResponse.pagination || {
            page,
            limit,
            total: apiResponse.data.length,
            is_active: is_active,
          },
        };
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return {
          data: apiResponse.body.data || [],
          pagination: {
            ...apiResponse.body.pagination,
            is_active: is_active,
          },
        };
      } else {
        throw new Error(
          apiResponse.body?.message ||
            apiResponse.message ||
            "Failed to fetch users",
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async createUser(userData: {
    companySlug?: string;
    companyId?: string;
    creatorUserId?: string;
    roleId: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    avatar?: string;
    is_active?: boolean;
    sales_revenue?: number;
    sales_target_value?: number;
    target_type?: string;
    pipeline_id?: string | null;
  }): Promise<{ data: User }> {
    try {
      // Lógica de respaldo: Si faltan datos críticos, intentar obtenerlos del localStorage
      let { companySlug, creatorUserId, companyId } = userData;

      if (!companyId || !creatorUserId) {
        try {
          const storedUserStr = localStorage.getItem("user");
          const storedCompanyStr = localStorage.getItem("company");

          if (!creatorUserId && storedUserStr) {
            const u = JSON.parse(storedUserStr);
            creatorUserId = u.id;
          }

          if (!companyId && storedCompanyStr) {
            const c = JSON.parse(storedCompanyStr);
            companyId = c.id;
          }
        } catch (e) {
          console.warn(
            "⚠️ Error intentando auto-completar datos desde localStorage:",
            e,
          );
        }
      }

      const finalData = { ...userData, company_id: companyId, creatorUserId };

      const response = await fetch(`${API_BASE_URL}/users`, {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(finalData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.body?.message ||
            errorData.message ||
            `HTTP error! status: ${response.status}`,
        );
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data) {
        return { data: apiResponse.data };
      } else if (
        (apiResponse.statusCode === 201 || apiResponse.statusCode === 200) &&
        apiResponse.body?.data
      ) {
        return { data: apiResponse.body.data };
      } else {
        throw new Error(
          apiResponse.body?.message ||
            apiResponse.message ||
            "Failed to create user",
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async updateUser(
    userId: string,
    slug: string,
    updaterUserId: string,
    userData: Partial<{
      roleId: string;
      email: string;
      firstName: string;
      lastName: string;
      phone?: string;
      avatar?: string;
      is_active?: boolean;
      sales_revenue?: number;
      sales_target_value?: number;
      target_type?: string;
      pipeline_id?: string | null;
    }>,
  ): Promise<{ data: User }> {
    try {
      const requestData = {
        slug,
        updaterUserId,
        targetUserId: userId,
        ...userData,
      };

      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.body?.message ||
            errorData.message ||
            `HTTP error! status: ${response.status}`,
        );
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data) {
        return { data: apiResponse.data };
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return { data: apiResponse.body.data };
      } else {
        throw new Error(
          apiResponse.body?.message ||
            apiResponse.message ||
            "Failed to update user",
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteUser(
    userId: string,
    slug: string,
    deleterUserId: string,
  ): Promise<{ success: boolean }> {
    try {
      const requestData = {
        slug,
        deleterUserId,
        targetUserId: userId,
      };

      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "DELETE",
        headers: this.getHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.body?.message ||
            errorData.message ||
            `HTTP error! status: ${response.status}`,
        );
      }

      const apiResponse = await response.json();

      if (apiResponse.success) {
        return { success: true };
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return { success: true };
      } else {
        throw new Error(
          apiResponse.body?.message ||
            apiResponse.message ||
            "Failed to delete user",
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async getUserById(userId: string): Promise<{ data: User }> {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: "GET",
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data) {
        return { data: apiResponse.data };
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return { data: apiResponse.body.data };
      } else {
        throw new Error(
          apiResponse.body?.message ||
            apiResponse.message ||
            "Failed to fetch user",
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(
    userId: string,
    slug: string,
    updaterUserId: string,
    newPassword: string,
  ): Promise<{ success: boolean }> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/users/${userId}`,
        {
          method: "PUT",
          headers: this.getHeaders(),
          body: JSON.stringify({ 
            slug,
            updaterUserId,
            targetUserId: userId,
            password: newPassword 
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.body?.message ||
            errorData.message ||
            `HTTP error! status: ${response.status}`,
        );
      }

      const apiResponse = await response.json();

      if (apiResponse.success) {
        return { success: true };
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        return { success: true };
      } else {
        throw new Error(
          apiResponse.body?.message ||
            apiResponse.message ||
            "Failed to reset password",
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async sendWelcomeEmail(userId: string): Promise<{ success: boolean }> {
    console.log(
      `📬 [usersService.sendWelcomeEmail] 1. Iniciando envío para ID de usuario: ${userId}`,
    );
    try {
      const url = `${API_BASE_URL}/users/${userId}/welcome-email`;
      console.log(
        `📬 [usersService.sendWelcomeEmail] 2. Llamando a: POST ${url}`,
      );

      const response = await fetch(url, {
        method: "POST",
        headers: this.getHeaders(),
      });

      console.log(
        "📬 [usersService.sendWelcomeEmail] 3. Respuesta cruda del servidor:",
        response,
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.body?.message ||
            errorData.message ||
            `HTTP error! status: ${response.status}`,
        );
      }

      const apiResponse = await response.json();

      if (apiResponse.success) {
        console.log(
          "✅ [usersService.sendWelcomeEmail] 4. El servidor respondió con éxito.",
        );
        return { success: true };
      } else if (apiResponse.statusCode === 200 && apiResponse.body?.success) {
        console.log(
          "✅ [usersService.sendWelcomeEmail] 4. El servidor respondió con éxito (formato anidado).",
        );
        return { success: true };
      } else {
        throw new Error(
          apiResponse.body?.message ||
            apiResponse.message ||
            "Failed to send welcome email",
        );
      }
    } catch (error) {
      console.error(
        "❌ [usersService.sendWelcomeEmail] 5. Error en el bloque catch:",
        error,
      );
      throw error;
    }
  }
}

export const usersService = new UsersService();
